/**
 * Address React Query Hooks
 * Hooks for fetching Philippine address data with cascading dependencies
 */

import { useQuery } from '@tanstack/react-query'
import { addressesApi } from '@/api/addresses'

// Query key factory
export const addressKeys = {
  all: ['addresses'] as const,
  provinces: () => [...addressKeys.all, 'provinces'] as const,
  cities: (provinceId: number) => [...addressKeys.all, 'cities', provinceId] as const,
  barangays: (cityId: number) => [...addressKeys.all, 'barangays', cityId] as const,
}

/**
 * Hook to fetch all provinces
 * Automatically loads on mount
 */
export function useProvinces() {
  return useQuery({
    queryKey: addressKeys.provinces(),
    queryFn: () => addressesApi.getProvinces(),
    staleTime: 1000 * 60 * 60, // 1 hour - provinces rarely change
  })
}

/**
 * Hook to fetch cities by province
 * Only fetches when provinceId is provided
 */
export function useCitiesByProvince(provinceId: number | null) {
  return useQuery({
    queryKey: addressKeys.cities(provinceId!),
    queryFn: () => addressesApi.getCitiesByProvince(provinceId!),
    enabled: !!provinceId, // Only fetch when province is selected
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}

/**
 * Hook to fetch barangays by city
 * Only fetches when cityId is provided
 */
export function useBarangaysByCity(cityId: number | null) {
  return useQuery({
    queryKey: addressKeys.barangays(cityId!),
    queryFn: () => addressesApi.getBarangaysByCity(cityId!),
    enabled: !!cityId, // Only fetch when city is selected
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}
